import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { resolveImage } from "@/lib/api";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.76, 0, 0.24, 1] },
  }),
};

export const Hero = ({ hero, business }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const logoY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const headlineLines = [hero.headline_line1, hero.headline_line2, hero.headline_line3].filter(Boolean);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" ref={ref} className="relative min-h-screen w-full overflow-hidden flex items-center">
      {/* Background */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10 scale-110">
        <img
          src={resolveImage(hero.background_url)}
          alt="Showroom"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/80" />
      </motion.div>

      <div className="max-w-7xl mx-auto w-full px-5 sm:px-8 pt-28 pb-20 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left: text */}
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="flex items-center gap-3 text-ad-red text-xs sm:text-sm font-bold uppercase tracking-[0.3em] mb-6"
          >
            <span className="h-px w-8 bg-ad-red" />
            {hero.eyebrow}
          </motion.p>

          <h1 className="font-display text-[15vw] sm:text-7xl lg:text-8xl leading-[0.88] tracking-tight uppercase">
            {headlineLines.map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 1 ? "metallic-text" : "text-white"}`}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed"
          >
            {hero.subheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("collections")}
              className="group px-8 py-4 bg-ad-red text-white font-bold uppercase tracking-widest text-sm hover:bg-ad-redhover transition-colors"
            >
              {hero.cta_primary}
            </button>
            <a
              data-testid="hero-cta-secondary"
              href={`https://www.google.com/maps?q=${encodeURIComponent(business.address)}`}
              target="_blank"
              rel="noreferrer"
              className="group px-8 py-4 border border-ad-silver/40 text-white font-bold uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-2"
            >
              <MapPin size={16} /> {hero.cta_secondary}
            </a>
          </motion.div>
        </motion.div>

        {/* Right: dynamic logo */}
        <motion.div style={{ y: logoY, opacity: fade }} className="lg:col-span-5 flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="relative"
          >
            <div className="absolute -inset-10 bg-ad-red/20 blur-3xl rounded-full" />
            <motion.img
              src={resolveImage(hero.logo_url)}
              alt={business.name}
              data-testid="hero-logo"
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative w-64 sm:w-80 lg:w-full max-w-md object-contain drop-shadow-[0_20px_60px_rgba(230,0,0,0.35)]"
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.button
        onClick={() => scrollTo("collections")}
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={18} />
      </motion.button>
    </section>
  );
};
